import { configure } from 'mobx';
import { UserManager, UserManagerSettings } from 'oidc-client';
import { Subject } from 'rxjs';

configure({ enforceActions: 'observed', isolateGlobalState: true });

export type Authentication = {
    readonly authority: string;
    readonly clientId: string;
    readonly redirectUrl: string;
    readonly additionalScopes: string;
    readonly loadUserInfo: string;
    readonly extraQueryParams: object;
};

export type ApplicationConfiguration = {
    readonly apiEndpoints: {
        readonly authorisation: string;
    };
    readonly authentication: Authentication;
    readonly cookieCheck: {
        readonly url: string;
    };
    readonly acceptedFileExtensions: string[];
    readonly maxFileSizeInBytes: number;
};

let config: ApplicationConfiguration;
let userManager: UserManager;

type Notification = {
    type: 'display' | 'error';
    message: string;
};

export const authorisationNotification$ = new Subject<Notification>();

export function displayMessage(message: string) {
    authorisationNotification$.next({
        type: 'display',
        message: message
    });
}

export function displayError(message: string) {
    authorisationNotification$.next({
        type: 'error',
        message: message
    });
}

export function createUserManagerSettings(appConfig: ApplicationConfiguration): UserManagerSettings {
    const authConfig: Authentication = {
        authority: appConfig.authentication.authority || '',
        clientId: appConfig.authentication.clientId || '',
        redirectUrl: appConfig.authentication.redirectUrl || '',
        additionalScopes: appConfig.authentication.additionalScopes || '',
        loadUserInfo: appConfig.authentication.loadUserInfo === 'false' ? 'false' : 'true',
        extraQueryParams: appConfig.authentication.extraQueryParams || {}
    };

    return {
        authority: authConfig.authority,
        client_id: authConfig.clientId,
        redirect_uri: authConfig.redirectUrl,
        response_type: 'id_token token',
        scope: `openid profile email ${authConfig.additionalScopes}`,
        post_logout_redirect_uri: `${authConfig.redirectUrl}#logged_out`,
        silent_redirect_uri: `${authConfig.redirectUrl}renew.html`,
        automaticSilentRenew: true,
        loadUserInfo: authConfig.loadUserInfo !== 'false',
        extraQueryParams: authConfig.extraQueryParams
    };
}

export function configureAuthorisation(settings: { appConfig: ApplicationConfiguration; userManager: UserManager }) {
    config = settings.appConfig;
    userManager = settings.userManager;
}

export const getConfig = (): ApplicationConfiguration => {
    const globalAppConfig = config;
    if (!!globalAppConfig) return globalAppConfig;

    throw new Error('No config has been set, please make sure your application calls configureAuthorisation');
};

export const getUserManager = (): UserManager => {
    if (userManager) return userManager;

    throw new Error('User Manager was not created, please make sure your application calls configureAuthorisation');
};
